/* eslint-disable @typescript-eslint/no-unsafe-argument */
'use client';

import React, { ChangeEvent, useEffect, useRef, useState, KeyboardEvent } from 'react';

import Modal from '@/lib/Modal';
import Input from '@/lib/Input';
import Button from '@/lib/Button';
import { CUSTOMER_ACCESS_TOKEN_KEY, OTP_REGEX } from '@/constants';
import { sendOtp, verifyOtp } from '@/api/login';
import SecuredByRazorpay from '@/lib/SecuredByRazorpay';
import { useGlobalStore } from '@/store';
import { fetchCustomerDetails } from '@/api/customer';
import { ApiFunctionResponse } from '@/api/types';
import { setCustomerAccessToken } from '@/utils/helpers';
import { updatePayment } from '@/api/payment';
import { trackSubmitOtpClicked } from '@/analytics/views/landing';
import { IUser } from '@/@types/globals';

interface OtpModalProps {
  isOpen: boolean;
  contact: string;
  onSuccess: (userRes: { data: IUser }) => void;
  onDismiss: () => void;
}

const OtpModal: React.FC<OtpModalProps> = ({ isOpen, contact, onDismiss, onSuccess }) => {
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [isSubmittingOtp, setIsSubmittingOtp] = useState(false);
  const [secondsToResendOtp, setSecondsToResendOtp] = useState(15);
  const reduceResendInterval = useRef<NodeJS.Timer | null>(null);

  const { paymentDetails, setUserAccessToken, setXDataToken, setUser, merchantKey } =
    useGlobalStore();
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { gateway_merchant } = paymentDetails.data ?? {};

  const resetResendOTPTimer = (): void => {
    setSecondsToResendOtp(15);

    reduceResendInterval.current = setInterval(() => {
      setSecondsToResendOtp((current) => {
        if (current > 0) {
          return current - 1;
        } else {
          clearTimeout(reduceResendInterval.current as NodeJS.Timer);
          return current;
        }
      });
    }, 1000);
  };

  useEffect(() => {
    if (contact) {
      sendOtp(contact, merchantKey as string);
      resetResendOTPTimer();

      return () => {
        clearTimeout(reduceResendInterval.current as NodeJS.Timer);
      };
    }
  }, []);

  const getUserDetails = async (token: string): Promise<void> => {
    const customerDetails = await fetchCustomerDetails({
      [CUSTOMER_ACCESS_TOKEN_KEY]: token,
    });

    if (customerDetails.success && customerDetails.data) {
      setUser(customerDetails.data);
      if (customerDetails.data.headers?.xHash) {
        setXDataToken(customerDetails.data.headers.xHash);
      }
      onSuccess({
        data: customerDetails.data,
      });
    } else {
      throw new Error('Error fetching customer');
    }
  };

  const submitOtp = async (): Promise<void> => {
    // Track Submit OTP clicked
    trackSubmitOtpClicked({
      contact,
    });

    if (OTP_REGEX.test(otp)) {
      setIsSubmittingOtp(true);

      const sendOtpResponse: ApiFunctionResponse = await verifyOtp(
        contact,
        otp,
        merchantKey as string,
      );
      if (sendOtpResponse.success) {
        // update payment with contact
        const res = updatePayment({
          contact,
        });
        setIsSubmittingOtp(true);
        if (sendOtpResponse.headers) {
          const xCustomerToken = sendOtpResponse.headers.xCustomerToken as string;
          setUserAccessToken(xCustomerToken);
          getUserDetails(xCustomerToken);
          // Timeout a refresh token every 25 mins so that even if session expires in between
          // a fresh Access token is always present
          setCustomerAccessToken(xCustomerToken, (token) => {
            setUserAccessToken(token);
          });
        }
      } else {
        setOtpError((sendOtpResponse.payload as string | undefined) ?? 'Unknown Error');
        setIsSubmittingOtp(false);
      }
    } else {
      setOtpError('Invalid OTP');
    }
  };

  const resendOtp = async (): Promise<void> => {
    resetResendOTPTimer();
    if (secondsToResendOtp === 0) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      await sendOtp(contact, merchantKey ?? '');
    }
  };

  const handleOtpInput = (e: ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;
    if (value.length > 6) {
      return;
    }
    setOtp(value);
    setOtpError('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      // submit otp on enter press
      submitOtp();
    }
  };

  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} className="d:pt-4">
      <Modal.Body>
        <div className="d:flex d:flex-col d:w-1/2 d:mt-6">
          <h2 className="font-tasa font-semibold text-2xl text-on-grey-normal mr-4">
            Enter the OTP sent to <br />
            {contact}
          </h2>

          <div className="mt-6 mb-4">
            <Input
              error={otpError}
              value={otp}
              onChange={handleOtpInput}
              onKeyDown={handleKeyDown}
              type="text"
              placeholder="Enter OTP"
            />
          </div>

          {secondsToResendOtp > 0 ? (
            <p className="text-base text-on-grey-muted">Resend OTP in : 00:{secondsToResendOtp}s</p>
          ) : (
            <button onClick={resendOtp} className="text-base text-on-grey-muted cursor-pointer">
              Resend OTP
            </button>
          )}

          <div className="mt-12 d:mt-6">
            <Button
              disabled={!OTP_REGEX.test(otp)}
              isLoading={isSubmittingOtp}
              loadingText="Submitting OTP"
              onClick={submitOtp}
            >
              Submit OTP
            </Button>
            <SecuredByRazorpay />
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default OtpModal;
